import React, { ReactNode } from "react";
import { FileQuestion } from "lucide-react";
import { cn } from "@/lib/utils";

interface TableEmptyProps {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: ReactNode;
  className?: string;
}

/**
 * TableEmpty - Empty state for tables and lists
 * 
 * Provides:
 * - Centered layout with icon, title, description
 * - Optional call-to-action button
 * - Customizable icon
 * 
 * @example
 * <TableEmpty
 *   title="No estimates found"
 *   description="Create your first estimate to get started"
 *   action={<Button>Create Estimate</Button>}
 * />
 */
const TableEmpty = ({
  title,
  description,
  action,
  icon,
  className,
}: TableEmptyProps) => {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center py-12 px-4 text-center",
        className
      )}
    >
      {/* Icon */}
      <div className="mb-4 text-muted-foreground">
        {icon || <FileQuestion className="h-12 w-12" />}
      </div>

      {/* Title */}
      <h3 className="text-lg font-medium font-montserrat text-foreground mb-1">
        {title}
      </h3>

      {/* Description */}
      {description && (
        <p className="text-sm text-muted-foreground max-w-sm mb-4">
          {description}
        </p>
      )}

      {/* Action */}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
};

export default TableEmpty;
